/**
 * Closeout · review recap before sending (wireframe 06).
 *
 * Supervisor/admin review the auto-assembled recap (crew, hours, tasks,
 * punch/RMA), edit a short narrative, then close + send — or save a draft.
 * Replaces the old "Preview recap → raw PDF" jump. Route:
 * /projects/:id/closeout.
 *
 * The narrative auto-draft is Pillar D.4; here it's editable text.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Download, ExternalLink, Loader2, Pencil, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  openRecapPdf,
  useCloseProject,
  useDraftRecapNarrative,
  useProjectRecap,
  useResolvePunchFromCloseout,
  useUpdateProject,
} from '@/api/projects';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog, PromptDialog } from '@/components/ui/Dialog';
import { RecapNotesCard } from '@/components/projects/RecapNotesCard';
import { RecapGalleryCurator } from '@/components/projects/RecapGalleryCurator';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { formatDate, formatMoneyCents } from '@/lib/format';

function fmtMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

/**
 * Status → chip tokens, matching TaskStatusControl / CrewTaskDetail so the
 * same status reads identically across screens (color by status, ADR-0010).
 * Covers task AND pickup statuses; anything unmapped renders neutral.
 */
const STATUS_CHIP: Record<string, string> = {
  scheduled: 'bg-status-scheduled text-status-scheduled-fg',
  en_route: 'bg-status-enroute text-status-enroute-fg',
  on_site: 'bg-status-onsite text-status-onsite-fg',
  in_progress: 'bg-status-onsite text-status-onsite-fg',
  paused: 'bg-status-pendingcloseout text-status-pendingcloseout-fg',
  blocked: 'bg-status-blocked text-status-blocked-fg',
  complete: 'bg-status-complete text-status-complete-fg',
  cancelled: 'bg-fill text-ink-dim',
};

/** Tiny status chip — distinguishes lifecycle state from the measurements beside it. */
function StatusChip({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ');
  return (
    <span
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize whitespace-nowrap ${
        STATUS_CHIP[status] ?? 'bg-fill text-ink-dim'
      }`}
    >
      {label}
    </span>
  );
}

export function ProjectCloseout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: recap, isLoading, error } = useProjectRecap(id);
  const { data: me } = useCurrentUser();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  const update = useUpdateProject(id ?? '');
  const close = useCloseProject(id ?? '');
  const draft = useDraftRecapNarrative(id ?? '');
  const resolvePunch = useResolvePunchFromCloseout(id ?? '');
  const [narrative, setNarrative] = useState('');
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // Punch item being closed from this screen — the modal collects the optional
  // resolution note the normal punch-list close flow expects. Carries the
  // pre-close status so an undo restores it exactly.
  const [closingItem, setClosingItem] = useState<{
    id: string;
    title: string;
    status: 'open' | 'assigned' | 'in_progress';
  } | null>(null);
  // Undo buffers for actions taken on this screen (misclick insurance): items
  // closed here stay listed with an Undo until you leave, and a cleared
  // blocking flag can be restored in place.
  const [recentlyClosed, setRecentlyClosed] = useState<
    Array<{ id: string; title: string; status: 'open' | 'assigned' | 'in_progress' }>
  >([]);
  const [clearedFlags, setClearedFlags] = useState<Record<string, boolean>>({});

  // Seed the textarea from the saved narrative once, unless the user is editing.
  useEffect(() => {
    if (recap && !dirty) setNarrative(recap.recap_narrative ?? '');
  }, [recap, dirty]);

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !recap) {
    return (
      <div className="p-6 text-sm text-status-blocked-fg">
        {(error as Error | undefined)?.message ?? 'Recap not found.'}
      </div>
    );
  }

  const isComplete = recap.status === 'complete';
  const openPunch = recap.punch_items_total - recap.punch_items_closed;
  // Tasks that aren't done — a close is still allowed (supervisor judgment),
  // but the confirm dialog names them so it never happens unknowingly.
  const openTasks = recap.tasks.filter(
    (t) => t.status !== 'complete' && t.status !== 'cancelled',
  ).length;
  // Open punch items flagged "blocks closeout" — the server refuses to close
  // while any remain, so don't offer a Close that can only fail.
  const blockingOpen = recap.punch_items_blocking_open ?? 0;
  // Items closed from this screen and no longer in the open list — rendered
  // struck-through with an Undo so a misclick is reversible in place.
  const openPunchIds = new Set((recap.open_punch_items ?? []).map((pi) => pi.id));
  const undoRows = recentlyClosed.filter((rc) => !openPunchIds.has(rc.id));

  async function downloadRecapPdf() {
    try {
      await openRecapPdf(recap!.project_id);
    } catch (e) {
      toast.error(`Could not open recap PDF: ${(e as Error).message}`);
    }
  }

  function saveDraft() {
    update.mutate(
      { recap_narrative: narrative.trim() || null },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Draft saved');
        },
        onError: (e) => toast.error(`Save failed: ${e.message}`),
      },
    );
  }

  function closeAndSend() {
    setConfirmClose(false);
    close.mutate(
      { recap_narrative: narrative.trim() || null },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Recap sent · project complete');
        },
        onError: (e) => toast.error(`Send failed: ${e.message}`),
      },
    );
  }

  return (
    <div className="p-6 max-w-5xl space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate(`/projects/${id}`)}
            className="flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink mb-1"
          >
            <ArrowLeft className="w-4 h-4" /> Project
          </button>
          <h1 className="text-2xl font-semibold">Review recap before sending</h1>
          <p className="text-ink-dim text-sm">
            {recap.title} · {recap.client_name}
            {recap.sap_order_number && (
              <span className="font-mono text-xs"> · Order {recap.sap_order_number}</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadRecapPdf}>
            <Download className="w-3.5 h-3.5" /> Download PDF
          </Button>
          <Button variant="outline" size="sm" onClick={saveDraft} disabled={update.isPending || !dirty}>
            {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{' '}
            {/* Post-send there's no "draft" — edits go live at the shared link. */}
            {isComplete ? 'Save changes' : 'Save draft'}
          </Button>
          {!isComplete && (
            <Button
              size="sm"
              onClick={() => setConfirmClose(true)}
              disabled={close.isPending || blockingOpen > 0}
              title={
                blockingOpen > 0
                  ? `${blockingOpen} punch item${blockingOpen === 1 ? '' : 's'} marked "blocks closeout" must be resolved first`
                  : undefined
              }
            >
              {close.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Close + send recap
            </Button>
          )}
        </div>
      </div>

      {/* The highest-stakes action in the app gets a real dialog, not a
          native confirm() (audit UX pass). Open tasks don't block a close
          (supervisor judgment — only blocks-closeout punch items do), so the
          dialog says exactly what closing over them means. */}
      <ConfirmDialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={closeAndSend}
        title="Close + send recap?"
        body={[
          openTasks > 0 &&
            `${openTasks} task${openTasks === 1 ? " isn't" : "s aren't"} complete — the project completes anyway and ${openTasks === 1 ? 'it appears' : 'they appear'} in the recap with ${openTasks === 1 ? 'its' : 'their'} current status.`,
          openPunch > 0 &&
            `${openPunch} punch item${openPunch === 1 ? '' : 's'} still open — ${openPunch === 1 ? 'it' : 'they'}'ll remain as a schedulable follow-up and ${openPunch === 1 ? 'is' : 'are'} listed in the recap.`,
          'The project is marked complete and the recap goes out to stakeholders now.',
        ]
          .filter(Boolean)
          .join(' ')}
        confirmLabel="Close + send"
        busy={close.isPending}
      />

      {/* Same close flow the punch list uses: an optional resolution note that
          lands in the item's audit trail (and the recap's description). */}
      <PromptDialog
        open={closingItem != null}
        onClose={() => setClosingItem(null)}
        title={`Close "${closingItem?.title ?? ''}"`}
        label="Resolution note (optional)"
        placeholder="e.g. Replaced gas cylinder; verified silent."
        hint="Recorded on the item — how it was resolved. Leave blank to close without a note."
        submitLabel="Close item"
        busy={resolvePunch.isPending}
        onSubmit={(note) => {
          if (!closingItem) return;
          const item = closingItem;
          resolvePunch.mutate(
            { id: item.id, action: 'close', note: note || undefined },
            {
              onSuccess: () => {
                setClosingItem(null);
                // Keep it on the list with an Undo — a misclicked close is
                // reversible without leaving the screen.
                setRecentlyClosed((list) => [...list.filter((x) => x.id !== item.id), item]);
                toast.success('Punch item closed');
              },
              onError: (e) => toast.error(`Could not close: ${e.message}`),
            },
          );
        }}
      />

      {blockingOpen > 0 && !isComplete && (
        <div className="rounded-md border border-status-blocked-fg/30 bg-status-blocked text-status-blocked-fg px-4 py-2 text-sm">
          {blockingOpen} punch item{blockingOpen === 1 ? '' : 's'} marked{' '}
          <span className="font-medium">blocks closeout</span>{' '}
          {blockingOpen === 1 ? 'is' : 'are'} still open — resolve{' '}
          {blockingOpen === 1 ? 'it' : 'them'} (or clear the flag) before sending the recap.
        </div>
      )}

      {isComplete && (
        <div className="rounded-md border border-status-complete-fg/30 bg-status-complete text-status-complete-fg px-4 py-2 text-sm flex items-center gap-2 flex-wrap">
          <CheckCircle2 className="w-4 h-4" /> Recap sent — changes you save here update the shared
          recap page immediately.
          {recap.completed_at && ` Closed ${formatDate(recap.completed_at)}.`}
          {recap.status_page_slug && (
            <a
              href={`/recap/${recap.status_page_slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium underline ml-1"
            >
              View the recap page recipients got <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {/* Auto-assembled preview */}
        <div className="md:col-span-2 space-y-4">
          <div className="bg-bg rounded-xl border border-line p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <Metric n={String(recap.tasks.length)} label="Tasks" />
            <Metric n={fmtMinutes(recap.total_duration_minutes)} label="Duration" />
            {recap.committed_man_minutes != null ? (
              <Metric
                n={fmtMinutes(recap.committed_man_minutes)}
                label="Committed man-hrs"
                sub={
                  recap.overage_man_minutes > 0
                    ? `+${fmtMinutes(recap.overage_man_minutes)} over`
                    : undefined
                }
              />
            ) : (
              <Metric n={fmtMinutes(recap.total_crewed_minutes)} label="Man-hours" />
            )}
            <Metric n={`${recap.punch_items_closed}/${recap.punch_items_total}`} label="Punch closed" />
            <ActualCostMetric
              cents={recap.actual_cost_cents}
              pending={update.isPending}
              onSave={(cents) =>
                update.mutate(
                  { actual_cost_cents: cents },
                  {
                    onSuccess: () => toast.success('Actual cost saved'),
                    onError: (e) => toast.error(`Could not save: ${e.message}`),
                  },
                )
              }
            />
          </div>

          {recap.committed_man_minutes != null && (
            <p className="text-xs text-ink-faint -mt-2 px-1">
              Committed {fmtMinutes(recap.committed_man_minutes)} man-hours
              {recap.overage_man_minutes > 0 ? (
                <span className="text-status-blocked-fg">
                  {' '}
                  · ran {fmtMinutes(recap.overage_man_minutes)} over (
                  {fmtMinutes(recap.total_crewed_minutes)} actual) — called out to the requester
                </span>
              ) : (
                <span> · {fmtMinutes(recap.total_crewed_minutes)} actual, within the committed floor</span>
              )}
            </p>
          )}

          {recap.crew_roster.length > 0 && (
            <Card title="Crew on this project">
              <div className="flex flex-wrap gap-4">
                {recap.crew_roster.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Avatar name={c.display_name} />
                    <div>
                      <div className="font-medium">{c.display_name}</div>
                      <div className="text-xs text-ink-faint">
                        {c.crew_role}
                        {c.is_lead && c.crew_role !== 'lead' && ' · lead'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {(recap.tasks.length > 0 || recap.project_worked_minutes > 0) && (
            <Card title="What was done">
              <ul className="space-y-2">
                {/* Direct project work (D-3/A5) — the crew worked the project
                    itself; without this line the totals above don't reconcile
                    with the rows (and a task-less project showed an empty card). */}
                {recap.project_worked_minutes > 0 && (
                  <li className="flex items-center justify-between gap-3 text-sm border-b border-line last:border-0 pb-2 last:pb-0">
                    <div className="min-w-0">
                      <span className="font-medium">On-site project work</span>
                      {(recap.project_crew ?? []).length > 0 && (
                        <span className="ml-2 text-xs text-ink-faint">
                          {(recap.project_crew ?? []).map((c) => c.display_name).join(', ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-ink-faint whitespace-nowrap">
                      <span>
                        {fmtMinutes(recap.project_worked_minutes)} ·{' '}
                        {fmtMinutes(
                          recap.project_worked_minutes * Math.max(1, (recap.project_crew ?? []).length),
                        )}{' '}
                        man-hrs
                      </span>
                    </div>
                  </li>
                )}
                {recap.tasks.map((t) => (
                  <li
                    key={t.id}
                    className="flex items-center justify-between gap-3 text-sm border-b border-line last:border-0 pb-2 last:pb-0"
                  >
                    <div className="min-w-0">
                      <span className="font-medium">{t.title}</span>
                      {t.sap_rma_reference && (
                        <span className="ml-2 text-xs font-mono text-ink-faint">
                          RMA {t.sap_rma_reference}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-ink-faint whitespace-nowrap">
                      <StatusChip status={t.status} />
                      {t.actual_duration_minutes != null && (
                        <span>
                          {fmtMinutes(t.actual_duration_minutes)} ·{' '}
                          {fmtMinutes(t.actual_duration_minutes * Math.max(1, t.crew?.length ?? 1))} man-hrs
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Warehouse pickups (workstream C) that serviced this project — for
              the record; a run can span several projects so its hours aren't in
              this project's totals. */}
          {(recap.pickups ?? []).length > 0 && (
            <Card title="Warehouse pickups">
              <ul className="space-y-1.5 text-sm">
                {(recap.pickups ?? []).map((p) => (
                  <li key={p.pickup_id} className="flex items-center justify-between gap-3">
                    <span className="font-medium min-w-0 truncate">{p.title}</span>
                    <span className="flex items-center gap-2 text-xs text-ink-faint whitespace-nowrap">
                      <span>
                        {p.scheduled_start ? new Date(p.scheduled_start).toLocaleDateString() : '—'}
                        {p.crew.length > 0 && ` · ${p.crew.map((c) => c.display_name).join(', ')}`}
                      </span>
                      <StatusChip status={p.status} />
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {recap.punch_items_total > 0 && (
            <Card title={`Punch list · ${recap.punch_items_closed}/${recap.punch_items_total} closed`}>
              {openPunch > 0 || undoRows.length > 0 ? (
                <div className="space-y-2">
                  {openPunch > 0 && (
                    <p className="text-xs text-ink-faint">
                      Resolve items right here — close what's done, or clear a blocking flag to send
                      the recap with the item left as a follow-up.
                    </p>
                  )}
                  <ul className="space-y-1.5">
                    {(recap.open_punch_items ?? []).map((pi) => (
                      <li
                        key={pi.id}
                        className="flex items-center justify-between gap-3 text-sm border-b border-line last:border-0 pb-1.5 last:pb-0"
                      >
                        <div className="min-w-0">
                          <span className="font-medium">{pi.title}</span>
                          {pi.blocks_recap && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-status-blocked text-status-blocked-fg px-1.5 py-0.5 text-[10px] font-medium">
                              Blocks closeout
                            </span>
                          )}
                          {!pi.blocks_recap && clearedFlags[pi.id] && (
                            <span className="ml-2 text-[10px] text-ink-faint">flag cleared</span>
                          )}
                          <div className="text-[11px] text-ink-faint truncate">{pi.task_title}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {pi.blocks_recap && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={resolvePunch.isPending}
                              onClick={() =>
                                resolvePunch.mutate(
                                  { id: pi.id, action: 'unblock' },
                                  {
                                    onSuccess: () => {
                                      setClearedFlags((f) => ({ ...f, [pi.id]: true }));
                                      toast.success('Blocking flag cleared — it stays a follow-up');
                                    },
                                    onError: (e) => toast.error(`Could not update: ${e.message}`),
                                  },
                                )
                              }
                            >
                              Clear flag
                            </Button>
                          )}
                          {!pi.blocks_recap && clearedFlags[pi.id] && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={resolvePunch.isPending}
                              title="Restore the blocks-closeout flag"
                              onClick={() =>
                                resolvePunch.mutate(
                                  { id: pi.id, action: 'reblock' },
                                  {
                                    onSuccess: () => {
                                      setClearedFlags(({ [pi.id]: _drop, ...rest }) => rest);
                                      toast.success('Blocking flag restored');
                                    },
                                    onError: (e) => toast.error(`Could not update: ${e.message}`),
                                  },
                                )
                              }
                            >
                              Undo
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={resolvePunch.isPending}
                            onClick={() =>
                              setClosingItem({
                                id: pi.id,
                                title: pi.title,
                                status: pi.status === 'closed' ? 'open' : pi.status,
                              })
                            }
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Close item
                          </Button>
                        </div>
                      </li>
                    ))}
                    {/* Just-closed-from-here items keep a row with an Undo — a
                        misclicked close is reversible without leaving. */}
                    {undoRows.map((rc) => (
                      <li
                        key={rc.id}
                        className="flex items-center justify-between gap-3 text-sm border-b border-line last:border-0 pb-1.5 last:pb-0"
                      >
                        <div className="min-w-0 flex items-center gap-1.5 text-ink-faint">
                          <CheckCircle2 className="w-3.5 h-3.5 text-status-complete-fg shrink-0" />
                          <span className="line-through truncate">{rc.title}</span>
                          <span className="text-[11px] shrink-0">closed just now</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resolvePunch.isPending}
                          title="Reopen this punch item (removes the close note)"
                          onClick={() =>
                            resolvePunch.mutate(
                              { id: rc.id, action: 'reopen', status: rc.status },
                              {
                                onSuccess: () => {
                                  setRecentlyClosed((list) => list.filter((x) => x.id !== rc.id));
                                  toast.success('Punch item reopened');
                                },
                                onError: (e) => toast.error(`Could not reopen: ${e.message}`),
                              },
                            )
                          }
                        >
                          Undo
                        </Button>
                      </li>
                    ))}
                  </ul>
                  {openPunch > 0 ? (
                    <p className="text-[11px] text-ink-faint">
                      Non-blocking open items stay as schedulable follow-ups and are listed in the recap.
                    </p>
                  ) : (
                    <div className="text-sm text-status-complete-fg">All punch items closed.</div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-status-complete-fg">All punch items closed.</div>
              )}
            </Card>
          )}

          {recap.rma_count > 0 && (
            <Card title="RMA summary">
              <div className="text-sm text-ink-dim">
                {recap.rma_count} RMA{recap.rma_count === 1 ? '' : 's'} recorded on this project.
              </div>
            </Card>
          )}

          {/* Recap photos — the supervisor curates the client-facing gallery
              from the crew's captured photos (opt-in + ordered). */}
          {canManage && (
            <RecapGalleryCurator
              projectId={recap.project_id}
              notes={recap.recap_notes ?? []}
              selectedGallery={recap.recap_gallery ?? []}
            />
          )}
          {/* Crew notes — read-only source material that informs the summary +
              the photo picks; never shown verbatim to the requester. */}
          <RecapNotesCard projectId={recap.project_id} variant="curate" />
        </div>

        {/* Narrative + recipients */}
        <div className="space-y-4">
          <Card title="Summary / message">
            <textarea
              value={narrative}
              onChange={(e) => {
                setNarrative(e.target.value);
                setDirty(true);
              }}
              placeholder="Short summary for the recap + the 'recap available' message…"
              rows={8}
              className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <div className="flex items-center justify-between gap-2 mt-2">
              <p className="text-xs text-ink-faint">
                Appears in the recap PDF and the notification.
              </p>
              {/* D.4: drafts INTO the textarea — the supervisor reads, edits,
                  and decides; the AI never writes the recap unsupervised. */}
              <Button
                variant="outline"
                size="sm"
                disabled={draft.isPending}
                onClick={() =>
                  draft.mutate(undefined, {
                    onSuccess: (r) => {
                      setNarrative(r.narrative);
                      setDirty(true);
                      toast.success(
                        r.source === 'stub'
                          ? 'Draft assembled from the recap facts (no AI key — stub mode)'
                          : 'Draft written from the recap — read it over before sending',
                      );
                    },
                    onError: (e) => toast.error(`Draft failed: ${e.message}`),
                  })
                }
              >
                {draft.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Draft with AI
              </Button>
            </div>
          </Card>

          <Card title="Recipients">
            <div className="text-sm space-y-1.5">
              <RecipientRow role="PM" name={recap.pm_display_name ?? '—'} />
              {recap.sales_executive && <RecipientRow role="Sales" name={recap.sales_executive} />}
              {recap.site_contact_name && (
                <RecipientRow role="Site contact" name={recap.site_contact_name} />
              )}
              <p className="text-xs text-ink-faint pt-1.5 border-t border-line mt-2">
                Each gets an email with the overview above, the recap PDF attached, and a link to
                the full web recap (your summary + the photos you picked). Delivered per the
                notification matrix when you send.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * The one closeout number entered by hand — editable in place (Ben 2026-08-27:
 * resolve everything on this screen), instead of a detour through the project's
 * Edit dialog. Dollars in, cents stored.
 */
function ActualCostMetric({
  cents,
  pending,
  onSave,
}: {
  cents: number | null;
  pending: boolean;
  onSave: (cents: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  if (!editing) {
    return (
      <button
        type="button"
        className="group text-center rounded-md px-1 -mx-1 hover:bg-fill-soft transition-colors"
        title="Click to enter the actual cost"
        onClick={() => {
          setValue(cents != null ? String(cents / 100) : '');
          setEditing(true);
        }}
      >
        <div className="inline-flex items-center gap-1 text-xl font-semibold tabular-nums group-hover:text-accent">
          <span className={cents == null ? 'text-ink-faint' : undefined}>
            {cents == null ? 'Add' : formatMoneyCents(cents)}
          </span>
          <Pencil className="w-3.5 h-3.5 text-ink-faint group-hover:text-accent" />
        </div>
        <div className="text-[11px] uppercase tracking-wide text-ink-faint group-hover:text-accent">
          Actual cost
        </div>
      </button>
    );
  }

  function commit() {
    const trimmed = value.trim();
    const dollars = trimmed === '' ? null : Number(trimmed.replace(/[$,]/g, ''));
    if (dollars != null && (!Number.isFinite(dollars) || dollars < 0)) return;
    onSave(dollars == null ? null : Math.round(dollars * 100));
    setEditing(false);
  }

  return (
    <div>
      <input
        autoFocus
        inputMode="decimal"
        value={value}
        disabled={pending}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={commit}
        placeholder="0.00"
        className="w-full max-w-[7rem] mx-auto text-center text-lg font-semibold tabular-nums rounded-md border border-accent bg-bg px-1 py-0.5 focus:outline-none"
      />
      <div className="text-[11px] uppercase tracking-wide text-ink-faint">Actual cost · $</div>
    </div>
  );
}

/**
 * Role label + name on distinct visual tracks (Ben 2026-08-27: the inline
 * "PM · Helena Waits" form read as one run of text — roles and names collided).
 */
function RecipientRow({ role, name }: { role: string; name: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-24 shrink-0 text-[10px] uppercase tracking-wide text-ink-faint">
        {role}
      </span>
      <span className="font-medium min-w-0 truncate">{name}</span>
    </div>
  );
}

function Metric({ n, label, sub }: { n: string; label: string; sub?: string }) {
  return (
    <div>
      <div className="text-xl font-semibold tabular-nums">{n}</div>
      <div className="text-[11px] uppercase tracking-wide text-ink-faint">{label}</div>
      {sub && <div className="text-[11px] text-status-blocked-fg tabular-nums">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg rounded-xl border border-line p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-3">{title}</div>
      {children}
    </div>
  );
}

